import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fmtTimeAgo } from "@/lib/labels";
import { ArrowDown, ArrowUp, ChevronLeft, Plus, Trash2, X, AlertTriangle } from "lucide-react";
import FunnelChart, { pct } from "@/components/journeys/FunnelChart";

const OPS = [
  { op: "eq", label: "is" },
  { op: "neq", label: "is not" },
  { op: "in", label: "is one of" },
  { op: "exists", label: "is set" },
];

const SETTINGS = [
  { key: "conversion_window_minutes", label: "Time to complete (minutes)",
    help: "How long someone has, from the first step, to reach the last." },
  { key: "eval_window_minutes", label: "Check every (minutes)",
    help: "Each check judges the people who started in this long a window." },
  { key: "baseline_days", label: "Baseline (days)",
    help: "Conversion over this many previous days is what normal looks like." },
  { key: "min_entrants", label: "Minimum people",
    help: "Fewer than this reaching a step and it is not judged: a quiet hour is not an outage." },
  { key: "drop_threshold_pct", label: "Break at a drop of (%)",
    help: "How far a step's conversion must fall, relative to baseline, to open a case." },
];

// How far back the preview looks. A month by default, so a flow people go
// through a few times a week still has a funnel to show.
const PREVIEW_RANGES = [7, 14, 30];

const EMPTY = {
  name: "", description: "", status: "draft",
  steps: [{ label: "", event_name: "", description: "", filters: [] },
          { label: "", event_name: "", description: "", filters: [] }],
  conversion_window_minutes: 60, eval_window_minutes: 60, baseline_days: 14,
  min_entrants: 30, drop_threshold_pct: 30,
};

export default function JourneyEditor() {
  const { id } = useParams();
  const isNew = id === "new";
  const nav = useNavigate();
  const { permissions } = useAuth();
  const canManage = permissions.includes("journey.manage");

  const [form, setForm] = useState(isNew ? EMPTY : null);
  const [saved, setSaved] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [catalogDays, setCatalogDays] = useState(30);
  const [previewDays, setPreviewDays] = useState(30);
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const timer = useRef(null);

  const load = async () => {
    const { data } = await api.get(`/journeys/${id}`);
    setSaved(data);
    setForm({
      name: data.name, description: data.description || "", status: data.status,
      steps: data.steps.map((s) => ({ ...s, filters: s.filters || [] })),
      ...Object.fromEntries(SETTINGS.map(({ key }) => [key, data[key]])),
    });
  };

  useEffect(() => {
    api.get("/journeys/event-catalog").then((r) => {
      setCatalog(r.data.events || []);
      if (r.data.days) setCatalogDays(r.data.days);
    }).catch(() => {});
    if (!isNew) load().catch((e) => setMsg(e?.response?.data?.message || "Could not load the journey"));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const propsFor = useMemo(() => {
    const index = {};
    catalog.forEach((e) => { index[e.event_name] = e.properties || []; });
    return index;
  }, [catalog]);

  // The preview follows the definition as it is edited, a moment after typing stops.
  const ready = form && form.steps.length >= 2 && form.steps.every((s) => s.event_name.trim());
  const previewKey = form && JSON.stringify([form.steps, form.conversion_window_minutes, previewDays]);
  useEffect(() => {
    if (!ready) { setPreview(null); return undefined; }
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.post("/journeys/preview", {
          steps: cleanSteps(form.steps),
          conversion_window_minutes: Number(form.conversion_window_minutes) || 60,
          days: previewDays,
        });
        setPreview(data);
        setPreviewError("");
      } catch (e) {
        setPreviewError(e?.response?.data?.message || "Preview failed");
      }
    }, 600);
    return () => clearTimeout(timer.current);
  }, [previewKey, ready]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!form) {
    return <div className="p-8 text-[13px]" style={{ color: "var(--text-muted)" }}>{msg || "Loading…"}</div>;
  }

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setStep = (i, patch) => set({ steps: form.steps.map((s, j) => (j === i ? { ...s, ...patch } : s)) });
  const move = (i, d) => {
    const steps = [...form.steps];
    [steps[i], steps[i + d]] = [steps[i + d], steps[i]];
    set({ steps });
  };
  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(""), 4000); };

  const save = async (status) => {
    setBusy(true);
    const body = {
      name: form.name, description: form.description, status: status || form.status,
      steps: cleanSteps(form.steps),
      ...Object.fromEntries(SETTINGS.map(({ key }) => [key, Number(form[key])])),
    };
    try {
      if (isNew) {
        const { data } = await api.post("/journeys", body);
        nav(`/journeys/${data.id}`, { replace: true });
      } else {
        await api.patch(`/journeys/${id}`, body);
        await load();
        flash(status === "active" ? "Active. Prioris checks it on every window." : "Saved.");
      }
    } catch (e) {
      const d = e?.response?.data;
      flash(d?.message ? `${d.message}${d.details?.step ? ` (step ${d.details.step})` : ""}` : "Could not save");
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm("Delete this journey and its history? Cases it opened stay.")) return;
    setBusy(true);
    try { await api.delete(`/journeys/${id}`); nav("/journeys"); }
    catch (e) { flash(e?.response?.data?.message || "Delete failed"); setBusy(false); }
  };

  const latest = saved?.latest;
  const brokenStep = latest?.broken_step_key && saved.steps.find((s) => s.key === latest.broken_step_key);

  return (
    <div className="px-4 py-5 md:px-8 md:py-6">
      <Link to="/journeys" className="text-[12px] inline-flex items-center gap-1 mb-3"
            style={{ color: "var(--text-secondary)" }}>
        <ChevronLeft size={13} /> Journeys
      </Link>

      <div className="flex items-start gap-4 mb-5">
        <div className="flex-1 min-w-0">
          <input className="input font-display text-[22px]" placeholder="Journey name, e.g. Checkout"
                 value={form.name} disabled={!canManage} data-testid="journey-name"
                 onChange={(e) => set({ name: e.target.value })} />
          <input className="input mt-2 text-[12.5px]" placeholder="What this journey is, for whoever gets the case"
                 value={form.description} disabled={!canManage}
                 onChange={(e) => set({ description: e.target.value })} />
        </div>
        {canManage && (
          <div className="flex gap-2 shrink-0">
            {!isNew && <button className="btn btn-ghost" onClick={remove} disabled={busy}><Trash2 size={13} /></button>}
            <button className="btn btn-ghost" onClick={() => save()} disabled={busy} data-testid="save-journey">
              {isNew ? "Save as draft" : "Save"}
            </button>
            {form.status === "active" ? (
              <button className="btn" onClick={() => save("paused")} disabled={busy || isNew}>Pause</button>
            ) : (
              <button className="btn" onClick={() => save("active")} disabled={busy || !ready}
                      data-testid="activate-journey">
                Activate
              </button>
            )}
          </div>
        )}
      </div>

      {msg && <div className="badge b-muted mb-3" data-testid="journey-msg">{msg}</div>}

      <div className="grid grid-cols-12 gap-5">
        <div className="col-span-12 lg:col-span-6 space-y-4">
          <div className="card p-4" data-testid="steps-editor">
            <div className="text-[11px] uppercase tracking-wider mb-3" style={{ color: "var(--text-muted)" }}>Steps, in order</div>
            <datalist id="journey-events">
              {catalog.map((e) => <option key={e.event_name} value={e.event_name}>{`${e.count} in ${catalogDays} days`}</option>)}
            </datalist>
            <div className="space-y-3">
              {form.steps.map((step, i) => (
                <div key={i} className="rounded-md p-3" style={{ border: "1px solid var(--border)" }}
                     data-testid={`step-${i}`}>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-[11px] w-4" style={{ color: "var(--text-muted)" }}>{i + 1}</span>
                    <input className="input" list="journey-events" placeholder="Event, e.g. Checkout Started"
                           value={step.event_name} disabled={!canManage}
                           data-testid={`step-event-${i}`}
                           onChange={(e) => setStep(i, { event_name: e.target.value })} />
                    <input className="input" placeholder="Label (optional)" value={step.label}
                           disabled={!canManage}
                           onChange={(e) => setStep(i, { label: e.target.value })} />
                    {canManage && (
                      <div className="flex shrink-0">
                        <button className="btn btn-ghost px-1.5" disabled={i === 0} onClick={() => move(i, -1)}
                                aria-label="Move up"><ArrowUp size={12} /></button>
                        <button className="btn btn-ghost px-1.5" disabled={i === form.steps.length - 1}
                                onClick={() => move(i, 1)} aria-label="Move down"><ArrowDown size={12} /></button>
                        <button className="btn btn-ghost px-1.5" disabled={form.steps.length <= 2}
                                onClick={() => set({ steps: form.steps.filter((_, j) => j !== i) })}
                                aria-label="Remove step"><X size={12} /></button>
                      </div>
                    )}
                  </div>

                  {/* Nothing computes with this. It is what tells a reader, and
                      the model explaining a break, what this step really is. */}
                  <input className="input text-[12px] mt-2 ml-6"
                         placeholder="What this step means (optional), e.g. the moment the card is charged"
                         value={step.description || ""} disabled={!canManage} maxLength={300}
                         data-testid={`step-description-${i}`}
                         onChange={(e) => setStep(i, { description: e.target.value })} />

                  {step.filters.map((f, k) => (
                    <div key={k} className="flex items-center gap-2 mt-2 pl-6">
                      <span className="text-[11px]" style={{ color: "var(--text-muted)" }}>where</span>
                      <input className="input text-[12px]" list={`props-${i}`} placeholder="property"
                             value={f.property} disabled={!canManage}
                             onChange={(e) => setStep(i, { filters: step.filters.map((g, m) => m === k ? { ...g, property: e.target.value } : g) })} />
                      <datalist id={`props-${i}`}>
                        {(propsFor[step.event_name] || []).map((p) => <option key={p} value={p} />)}
                      </datalist>
                      <select className="input text-[12px] w-auto" value={f.op} disabled={!canManage}
                              onChange={(e) => setStep(i, { filters: step.filters.map((g, m) => m === k ? { ...g, op: e.target.value } : g) })}>
                        {OPS.map((o) => <option key={o.op} value={o.op}>{o.label}</option>)}
                      </select>
                      {f.op !== "exists" && (
                        <input className="input text-[12px]" placeholder={f.op === "in" ? "a, b, c" : "value"}
                               value={Array.isArray(f.value) ? f.value.join(", ") : (f.value ?? "")}
                               disabled={!canManage}
                               onChange={(e) => setStep(i, { filters: step.filters.map((g, m) => m === k ? { ...g, value: e.target.value } : g) })} />
                      )}
                      {canManage && (
                        <button className="btn btn-ghost px-1.5" aria-label="Remove filter"
                                onClick={() => setStep(i, { filters: step.filters.filter((_, m) => m !== k) })}>
                          <X size={12} />
                        </button>
                      )}
                    </div>
                  ))}
                  {canManage && step.filters.length < 5 && (
                    <button className="btn btn-ghost text-[11.5px] mt-1 ml-5"
                            onClick={() => setStep(i, { filters: [...step.filters, { property: "", op: "eq", value: "" }] })}>
                      <Plus size={11} /> Filter on a property
                    </button>
                  )}
                </div>
              ))}
            </div>
            {canManage && form.steps.length < 10 && (
              <button className="btn btn-ghost mt-3" data-testid="add-step"
                      onClick={() => set({ steps: [...form.steps, { label: "", event_name: "", description: "", filters: [] }] })}>
                <Plus size={13} /> Add step
              </button>
            )}
            {catalog.length === 0 && (
              <p className="text-[11.5px] mt-3" style={{ color: "var(--text-muted)" }}>
                No events have arrived in the last {catalogDays} days. Connect your analytics under{" "}
                <Link to="/connectors" className="underline">Connectors</Link> to pick from real event names.
              </p>
            )}
          </div>

          <div className="card p-4">
            <div className="text-[11px] uppercase tracking-wider mb-3" style={{ color: "var(--text-muted)" }}>When it counts as broken</div>
            <div className="grid grid-cols-2 gap-3">
              {SETTINGS.map(({ key, label, help }) => (
                <label key={key} className="block">
                  <span className="text-[12px]">{label}</span>
                  <input className="input mt-1" type="number" value={form[key]} disabled={!canManage}
                         onChange={(e) => set({ [key]: e.target.value })} />
                  <span className="text-[11px] block mt-0.5 leading-snug" style={{ color: "var(--text-muted)" }}>{help}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="col-span-12 lg:col-span-6 space-y-4">
          {latest && (
            <div className="card p-4" data-testid="latest-evaluation">
              <div className="flex items-center gap-2 mb-3">
                <div className="text-[11px] uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Latest check</div>
                <span className="text-[11px]" style={{ color: "var(--text-secondary)" }}>
                  {fmtTimeAgo(latest.window_end)} · v{latest.journey_version}
                </span>
                {latest.case_id && (
                  <Link to={`/cases/${latest.case_id}`} className="ml-auto badge b-rev" data-testid="journey-case-link">
                    <AlertTriangle size={10} className="inline mr-0.5" /> Open case
                  </Link>
                )}
              </div>
              <FunnelChart
                steps={saved.steps.map((s) => ({ key: s.key, label: s.label,
                  count: latest.step_counts[s.key] || 0, conversion: latest.step_conversion[s.key] }))}
                baseline={latest.baseline_conversion}
                brokenKey={latest.broken_step_key}
              />
              {brokenStep && saved.stuck?.people?.length > 0 && (
                <div className="mt-4" data-testid="stuck-list">
                  <div className="text-[11px] uppercase tracking-wider mb-1.5" style={{ color: "var(--text-muted)" }}>
                    Stuck before “{brokenStep.label}” · {saved.stuck.people.length}
                  </div>
                  <div className="max-h-48 overflow-y-auto text-[12px] space-y-1">
                    {saved.stuck.people.map((p) => (
                      <div key={p.subject} className="flex items-center gap-2">
                        {p.customer_id ? (
                          <Link to={`/customers/${p.customer_id}`} className="underline">
                            {p.customer_name || p.subject}
                          </Link>
                        ) : (
                          <span className="font-mono" style={{ color: "var(--text-secondary)" }}>{p.subject}</span>
                        )}
                        <span className="ml-auto text-[11px]" style={{ color: "var(--text-muted)" }}>
                          {fmtTimeAgo(p.reached_previous_at)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="card p-4" data-testid="journey-preview">
            <div className="flex items-center gap-2 mb-3">
              <div className="text-[11px] uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>
                Preview · last
              </div>
              <div className="flex gap-1" data-testid="preview-range">
                {PREVIEW_RANGES.map((d) => (
                  <button key={d} className={`btn ${d === previewDays ? "" : "btn-ghost"} px-1.5 py-0 text-[11px]`}
                          onClick={() => setPreviewDays(d)} data-testid={`preview-days-${d}`}>
                    {d}d
                  </button>
                ))}
              </div>
              {preview && (
                <span className="text-[11px] ml-auto" style={{ color: "var(--text-secondary)" }}>
                  {preview.entrants.toLocaleString()} started · {pct(preview.completion)} completed
                </span>
              )}
            </div>
            {!ready && (
              <p className="text-[12px]" style={{ color: "var(--text-muted)" }}>
                Give every step an event and the funnel appears here.
              </p>
            )}
            {previewError && <p className="text-[12px]" style={{ color: "var(--risk-rev)" }}>{previewError}</p>}
            {ready && preview && <FunnelChart steps={preview.steps} />}
          </div>
        </div>
      </div>
    </div>
  );
}

function cleanSteps(steps) {
  return steps.map((s) => ({
    ...(s.key ? { key: s.key } : {}),
    label: s.label?.trim() || s.event_name.trim(),
    event_name: s.event_name.trim(),
    filters: (s.filters || []).filter((f) => f.property?.trim()),
  }));
}
